import React from "react";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import GraphicEqOutlinedIcon from "@mui/icons-material/GraphicEqOutlined";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import DeviceThermostatOutlinedIcon from "@mui/icons-material/DeviceThermostatOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";

import Accelerometer from "../components/SensorGraphs/Accelerometer";
import ECG from "../components/SensorGraphs/ECG";
import HeartRate from "../components/SensorGraphs/HeartRate";
import PPG from "../components/SensorGraphs/PPG";
import SPO2 from "../components/SensorGraphs/SPO2";
import SkinTemperature from "../components/SensorGraphs/SkinTemperature";
import ElectrodermalActivity from "../components/SensorGraphs/ElectrodermalActivity";

/**
 * Single catalogue of the metrics the app can show.
 *
 * Dashboard and Reports both used to keep their own copies of this list, which
 * is how their icons, labels and ordering drifted apart. They now render from
 * here, so a metric looks and is named the same everywhere.
 *
 * `id` is the stable key persisted to localStorage; `label` is display-only
 * and safe to reword.
 */
export const METRICS = [
  {
    id: "Heart Rate",
    label: "Heart rate",
    short: "Heart rate",
    hint: "Beats per minute over time",
    sensorCode: 3,
    colorToken: "--wg-metric-heartrate",
    icon: <FavoriteBorderOutlinedIcon />,
    render: (props) => <HeartRate {...props} />,
  },
  {
    id: "ECG (Electrocardiogram)",
    label: "ECG",
    short: "ECG",
    hint: "Electrocardiogram waveform",
    sensorCode: 1,
    colorToken: "--wg-metric-ecg",
    icon: <MonitorHeartOutlinedIcon />,
    render: (props) => <ECG {...props} />,
  },
  {
    id: "SPO2 (Blood Oxygen Saturation)",
    label: "Blood oxygen",
    short: "SpO2",
    hint: "Oxygen saturation percentage",
    sensorCode: 4,
    colorToken: "--wg-metric-spo2",
    icon: <WaterDropOutlinedIcon />,
    render: (props) => <SPO2 {...props} />,
  },
  {
    id: "Skin Temperature",
    label: "Skin temperature",
    short: "Skin temp",
    hint: "Surface temperature against target",
    sensorCode: 2,
    colorToken: "--wg-metric-skintemp",
    icon: <DeviceThermostatOutlinedIcon />,
    render: (props) => <SkinTemperature {...props} />,
  },
  {
    id: "PPG (Photoplethysmogram)",
    label: "PPG",
    short: "PPG",
    hint: "Red, infrared and green waveforms",
    sensorCode: 5,
    colorToken: "--wg-metric-ppg",
    icon: <GraphicEqOutlinedIcon />,
    render: (props) => <PPG {...props} />,
  },
  {
    id: "Accelerometer",
    label: "Accelerometer",
    short: "Motion",
    hint: "X, Y and Z motion vectors",
    sensorCode: 0,
    colorToken: "--wg-metric-accelerometer",
    icon: <ShowChartOutlinedIcon />,
    render: (props) => <Accelerometer {...props} />,
  },
  {
    id: "Electrodermal Activity (EDA)",
    label: "Electrodermal activity",
    short: "EDA",
    hint: "Skin conductance in microsiemens",
    sensorCode: 7,
    colorToken: "--wg-metric-eda",
    icon: <BoltOutlinedIcon />,
    render: (props) => <ElectrodermalActivity {...props} />,
  },
];

export const METRICS_BY_ID = METRICS.reduce((map, metric) => {
  map[metric.id] = metric;
  return map;
}, {});

export const getMetric = (id) => METRICS_BY_ID[id] ?? null;

export default METRICS;
